/* The layer model.
 *
 * This header used to credit the design to "signalk-binnacle", citing
 * `src/shared/map/types.ts`. THERE IS NO SUCH PROJECT — checked 3 Aug 2026: no
 * repository of that name, no code-search hit for those files, nothing on the
 * web but a boat fitting and a chandlery. The citation was invented, and the
 * comparison against freeboard-sk that came with it was never made. The design
 * below is ours and stands on its own: bands are named, ordering is
 * declarative, and a layer can be added or removed without anyone having to
 * know what number to pick.
 *
 * The key idea is the BAND. Every layer belongs to one, the bands have a fixed
 * order, and MapLibre's `beforeId` is used to insert into the right one — so
 * "AIS is above the chart but below the boat" is a property of the band, not of
 * an integer somebody has to keep consistent.
 */

import type { Map as MlMap } from 'maplibre-gl';

import type { Bbox } from '../lib/geo.ts';
import type { SourceName } from '../sources.ts';

export type ZBand =
  | 'basemap'
  | 'bathymetry'
  /* The sea surface, which is a real thing in a pitched view: the seabed is
   * drawn BELOW it and everything that floats is drawn ON it. In the flat chart
   * this band is simply empty. */
  | 'seasurface'
  | 'weather'
  /* Fishing zones and the rockfish conservation areas: a management/regulation
   * overlay, under the navigation marks so a buoy always wins the glance. */
  | 'fishing'
  | 'seamarks'
  | 'poi'
  | 'tides'
  | 'safety'
  | 'traffic'
  | 'track'
  | 'routes'
  | 'vessel'
  | 'overlay-top';

/** Bottom to top. The order here IS the stacking order. */
export const Z_ORDER: readonly ZBand[] = [
  'basemap',
  'bathymetry',
  'seasurface',
  'weather',
  'fishing',
  'seamarks',
  'poi',
  'tides',
  'safety',
  'traffic',
  'track',
  'routes',
  'vessel',
  'overlay-top'
];

/** Which section of the layers panel a layer appears under.
 *
 * BY SUBJECT, NOT BY PROVENANCE. The original split was `live` versus the
 * rest, which made sense when `live` held three things. It grew to nine — tide
 * stations, tidal streams, wind stations, wind field, wind forecast, wind
 * arrows, weather zones, AIS, notes — and "which of these is the wind one"
 * became a scrolling exercise. What a layer IS (weather, tide, traffic) is the
 * question you actually arrive with; whether it happens to come off a live
 * feed is a property of the row, and the row already says so. */
export type LayerCategory =
  | 'mine'
  | 'charts'
  | 'weather'
  | 'tides'
  | 'fishing'
  | 'traffic'
  | 'reference';

export interface MapThemePaint {
  night: boolean;
}

export interface LayerContext {
  map: MlMap;
  /** The id to pass as MapLibre's `beforeId` so a layer lands in its band. */
  beforeIdFor(band: ZBand): string | undefined;
}

export interface LayerModule {
  id: string;
  title: string;
  band: ZBand;
  category: LayerCategory;
  description?: string;

  /** Nested under a parent in the panel; hidden when the parent is off. */
  parent?: string;
  /** Only one layer in a group may be visible at a time. */
  exclusive?: string;

  supportsOpacity: boolean;
  defaultVisible?: boolean;
  defaultOpacity?: number;

  /** Cannot be hidden or reordered — own vessel, alarms. */
  pinned?: boolean;
  /** Not shown as a togglable row (a tool rather than a layer). */
  listed?: boolean;

  /** MapLibre layer ids this module owns, bottom to top. */
  layerIds: readonly string[];

  add(ctx: LayerContext): void | Promise<void>;
  remove(ctx: LayerContext): void;
  setVisible(ctx: LayerContext, visible: boolean): void;
  setOpacity?(ctx: LayerContext, opacity: number): void;
  applyTheme?(ctx: LayerContext, paint: MapThemePaint): void;
  /** Re-add after a base style swap, which drops every layer. */
  reattach?(ctx: LayerContext): void | Promise<void>;

  /**
   * Named data sources this layer cannot work without.
   *
   * A host that provides none of them is not a broken host — a public chart
   * genuinely has no SignalK server, and a boat at anchor with the radio off
   * genuinely has no marine-data. Declaring the dependency lets the panel say
   * so, through the same greyed-row-with-a-reason path that already handles a
   * provider going offline. The alternative is a layer that renders nothing
   * and cannot explain itself, which is what "the new overlays don't work"
   * reports have always turned out to be.
   *
   * This is checked by the manager, so a module does not have to write an
   * `available()` for it. Both compose: a layer may require a source AND have
   * its own reason to be unavailable.
   */
  requires?: readonly SourceName[];

  /**
   * Where in the world this layer has anything to say.
   *
   * ═══ THE WHOLE REGION STORY IS THIS ONE FIELD ═══
   *
   * Almost everything on this chart is Canadian: CHS tidal streams, DFO fishing
   * subareas, EC marine zones, a SalishSeaCast model that stops at the Strait of
   * Juan de Fuca. Sail south and they have nothing to draw — and before this
   * they drew nothing, silently, which is indistinguishable from broken.
   *
   * A layer whose coverage does not intersect the current view is UNAVAILABLE
   * with a reason, through exactly the same path as a missing provider. Adding
   * a region therefore means registering layers with a different coverage and a
   * different data source; it does not mean touching any layer that already
   * exists.
   *
   * Absent means global — the boat, the track, the route you are drawing.
   *
   * A FUNCTION is usually the better answer than a constant, because most of
   * these datasets already know their own extent: the tide and current stores
   * hold station lists, the NEMO lattices carry their own corners. Returning
   * null means "not known yet", which is treated as covering — a layer must not
   * grey out for the second between mount and its data arriving.
   */
  coverage?: Coverage;
  /** Plain-language reason used when the current view misses fixed coverage. */
  outOfCoverageHint?: string;

  /** False greys the row out rather than hiding the capability. */
  available?(): boolean;
  unavailableHint?: string;
}

/** A fixed box, or one derived from data that may not have loaded yet. */
export type Coverage = Bbox | (() => Bbox | null);

export interface LayerState {
  visible: boolean;
  opacity: number;
}

/** Modules that carry live data and need poking each tick. */
export interface Updatable {
  update(ctx: LayerContext): void;
}

/* Lives HERE rather than beside the modules it tests, and the three lines are
 * load-bearing: modules.ts is 1,800 lines of layer definitions that are only
 * reachable once MapLibre has loaded, and a static import of one guard from it
 * pinned the whole file to the critical path that blocks first paint. */
export function isUpdatable(m: LayerModule): m is LayerModule & Updatable {
  return typeof (m as Partial<Updatable>).update === 'function';
}
